import { useRef, useState } from 'react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTranslations } from '../../../../App/i18n/i18nSlice';
import { RootState } from '../../../../App/store';
import { EditorModifier, RoleType, RoomType } from '../../../../App/enums';
import EditorTextArea from '../../../Shared/Components/Editor/EditorTextArea';
import { Editor as TinyMCEEditor } from "tinymce"
import CharacterCounter from '../../../Shared/Components/CharacterCounter';
import { FileIcon, SendIcon, SmileIcon } from '../../../Shared/Components/SvgIcons';
import EmojiPickerPortal from '../../../Shared/Components/EmojiPickerPortal';
import { acceptedFileTypes, fileImageType } from "../../../../App/GlobaleVariables"
import { useNavigate, useParams } from 'react-router';
import { MentionedUsersType } from '../../../../Types/RoomPosts';
import { ConvertStringToHTML } from '../../../../App/Helpers/Helpers';
import { hashtagRegex } from '../../../../App/Regex';
import { roomPostPostRequest } from '../../RoomPost/RoomPostRequests';
import useUnsavedChangesWarning from '../../../Shared/Hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../../../Shared/Components/UnsavedChangesModal';


const RoomPostTab: React.FC = () => {
    const { roomId } = useParams()
    const organization = useSelector((state: RootState) => state.organizationsSlice.organizationSlice.organization)
    const roomDataState = useSelector((state: RootState) => state.roomSlice.roomSlice.room)
    const dispatch = useDispatch()
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const translation = useSelector(selectTranslations)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    let inputRef = React.useRef<HTMLInputElement>(null)
    const navigate = useNavigate()

    const posts = useSelector((state: RootState) => state.RoomPostsSlice.roomPostsSlice.posts)
    const isMemberInRoom = useSelector((state: RootState) => state.MemberInRoomStatusSlice.MemberInRoomStatusSlice.isMember)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const photoziseErrorState = useSelector((state: RootState) => state.roomSlice.photoSizeErrorSlice.message)
    const postContentErrorStatus = useSelector((state: RootState) => state.RoomPostsSlice.roomPostContentErrorSlice.message)


    const editorRef = useRef<TinyMCEEditor | null>(null)
    const [content, setContent] = useState<string>("")
    const [inputState, setInputState] = useState<string>("")
    const [image, setImage] = useState<any>(null)
    const [documentState, setDocumentState] = useState<any>(null)
    const [previewUrl, setPreviewUrl] = useState<string>()
    const contentMaxLength = 2000
    const [fileType, setFileType] = useState("")
    const [fileName, setFileName] = useState<string>()
    const [isEmojiSelectorOpen, setIsEmojiSelectorOpen] = useState(false)
    const emojiButtonRef = useRef<HTMLDivElement>(null)
    const hasUnsavedPostChanges = !isSubmitting && (content.trim().length > 0 || image !== null || documentState !== null)

    const {
        isUnsavedChangesModalOpen,
        confirmNavigation,
        cancelNavigation,
    } = useUnsavedChangesWarning(hasUnsavedPostChanges)

    const onEmojiClick = (emojiObject: any, event?: { preventDefault: () => void }) => {
        event?.preventDefault()
        if (editorRef.current) {
            editorRef.current.insertContent(emojiObject.emoji)
        }
        setIsEmojiSelectorOpen(false)
    }

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files

        if (files) {
            const reader = new FileReader()
            reader.readAsDataURL(files[0])

            reader.addEventListener("load", () => {
                if (fileImageType.includes(files[0].type)) {
                    setImage(files[0])
                    setDocumentState(null)
                } else {
                    setDocumentState(files[0])
                    setImage(null)
                }
                setFileType(files[0].type)
                setFileName(files[0].name)
                setPreviewUrl(URL.createObjectURL(files[0]))
            })
        }
    }

    const onPostSubmit = async () => {
        setIsSubmitting(true)
        const objectId = role === RoleType.USER ? userDataState.id : organization?.id
        const mentionedUsersArray: MentionedUsersType[] = []
        let modifiedContent = ""
        if (editorRef.current!) {
            const htmlContent = ConvertStringToHTML(editorRef!.current!.getContent({ format: 'raw' }))
            const tagSpans = htmlContent.querySelectorAll("[data-type]")

            tagSpans.forEach((span, index) => {
                span.innerHTML = "@tagged"
                mentionedUsersArray.splice(index, 0, {
                    tagged_user: span.getAttribute("data-type") === RoleType.USER.toLocaleLowerCase() ? parseInt(span.id) : null,
                    tagged_org: span.getAttribute("data-type") === RoleType.ORGANIZATION.toLocaleLowerCase() ? parseInt(span.id) : null,
                })
                modifiedContent = htmlContent.outerHTML.replaceAll("<body>", "").replaceAll("</body>", "").replace(hashtagRegex, "$1")
            })

            if (mentionedUsersArray.length >= 1 || editorRef.current!.getContent({ format: "text" }).length > 0) {
                const formData = new FormData()

                formData.append("content", modifiedContent.length > 0 ? modifiedContent : editorRef!.current!.getContent({ format: 'raw' }).replace(hashtagRegex, "$1"))
                formData.append("content_type", role.toLowerCase())
                formData.append("object_id", objectId?.toString()!)
                formData.append("tags", JSON.stringify(mentionedUsersArray))

                formData.append("room", roomId!)
                image !== null && formData.append("post_file", image)
                documentState !== null && formData.append("post_file", documentState)
                fileType.length > 0 && formData.append("file_type", fileType)
                await dispatch<any>(roomPostPostRequest(formData, posts))

                switch (role) {
                    case RoleType.USER:
                        navigate(`/room/${roomId!}`)
                        window.scrollTo(0, 0)
                        break
                    case RoleType.ORGANIZATION:
                        navigate(`/room/${organization?.id}/${roomId!}`)
                        window.scrollTo(0, 0)
                        break
                    default:
                        break
                }
            }
        }
        setIsSubmitting(false)
    }

    const removeFile = () => {
        setPreviewUrl("")
        setImage(null)
        setDocumentState(null)
        setFileName("")
        if (inputRef.current) {
            inputRef.current.value = ""
        }
    }
    
    if (roomDataState.type === RoomType.Private && (isMemberInRoom === "pending" || isMemberInRoom === "non-member")) {
        return <div className="room-layout__right-panel-content">
             <div className="room-layout__no-access">{translation.cantSeeRoomContent}</div>
        </div>
    }

    return (
        <div className="room-layout__right-panel-content room-layout__right-panel-content--new-post">
            <UnsavedChangesModal
                isOpen={isUnsavedChangesModalOpen}
                title={translation.unsavedChangesTitle}
                message={translation.unsavedChangesMessage}
                cancelLabel={translation.cancel}
                confirmLabel={translation.leavePage}
                onCancel={cancelNavigation}
                onConfirm={confirmNavigation}
            />
            <div className="room-post-editor">
                {roomDataState.is_active && (roomDataState.type === RoomType.Public || isMemberInRoom) && (
                    <div className="room-post-editor__content">
                        <div className="room-post-editor__editor-area">
                            <EditorTextArea
                                inputState={inputState}
                                setInputState={setInputState}
                                editorRef={editorRef}
                                editorPlaceholder={translation.typeYourMessage}
                                setContent={setContent}
                                editorHeight={100}
                                modifier={EditorModifier.roomPostTab}
                                initialEditorValue=""
                                enterPressed={() => { }}
                            />
                            <CharacterCounter max={2000} current={content.length} modifier="post" />
                        </div>
                        <div className="room-post-editor__publishing-info">
                            Dein Beitrag wird im Raum <span className="room-post-editor__room-name">{roomDataState.name}</span> veröffentlicht.
                            {roomDataState.type === RoomType.Public && (
                                ' Dieser Raum ist öffentlich. Dein Beitrag ist daher für alle edusiia Mitglieder sichtbar.'
                            )}
                        </div>

                        {postContentErrorStatus.length > 0 && <p className="room-layout__error-message">{postContentErrorStatus}</p>}
                        {photoziseErrorState && <p className="crop__error-message">{translation.fileIsTooBig}</p>}
                        <div className="room-post-editor__right-panel-icons-container">
                            {fileName && (
                                <div className='room-post-editor__file'>
                                    Ausgewählte Datei: <br />
                                    {fileName} <span onClick={removeFile} style={{ cursor: "pointer", marginLeft: "0.5rem", color: "red" }}>✖</span>
                                </div>
                            )}
                           {/*  <div
                                ref={emojiButtonRef}
                                onClick={() => setIsEmojiSelectorOpen(!isEmojiSelectorOpen)}
                                className="room-post-editor__right-panel-icon"
                            >
                                <SmileIcon />
                            </div>
                            <EmojiPickerPortal
                                isOpen={isEmojiSelectorOpen}
                                onEmojiClick={onEmojiClick}
                                onClose={() => setIsEmojiSelectorOpen(false)}
                                triggerElementRef={emojiButtonRef as any}
                            /> */}
                            <div className="room-post-editor__right-panel-icon">
                                <input className="room-layout__input-file" type="file" onChange={onFileChange} accept={acceptedFileTypes} ref={inputRef} />
                                <FileIcon />
                            </div>
                            {content.length > 0 && content.length < contentMaxLength + 1 && !isSubmitting ? (
                                <div className="room-post-editor__right-panel-icon room-post-editor__right-panel-icon" onClick={onPostSubmit}>
                                    <SendIcon color="green" />
                                </div>
                            ) : (
                                <div className="room-post-editor__right-panel-icon room-post-editor__right-panel-icon--disable">
                                    <SendIcon color="grey" />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RoomPostTab;